import { Button } from '@/components/ui/button';
import { useGetChatCredentials } from '@/hooks/chat/useGetChatCredentials';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useStreamVideoClient } from '@stream-io/video-react-sdk';
import { reportError } from '@/utils/errorReporter';
import { useTranslation } from 'react-i18next';

export const IncomingVideocallBanner = () => {
  const { data: chatCredentials } = useGetChatCredentials();
  const videoClient = useStreamVideoClient();

  if (!videoClient || !chatCredentials) {
    return <></>;
  }

  return <ReadyIncomingVideocallBanner />;
};

const ReadyIncomingVideocallBanner = () => {
  const { data: chatCredentials } = useGetChatCredentials();
  const videoClient = useStreamVideoClient();
  const [isCallActive, setIsCallActive] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const eventListenerRef = useRef<(() => void) | null>(null);
  const setupAttemptsRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const retrySetup = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    const delay = Math.min(1000 * Math.pow(2, setupAttemptsRef.current), 10000); // 1, 2, 4, 8 sec

    retryTimeoutRef.current = setTimeout(() => {
      setupAttemptsRef.current += 1;
    }, delay);
  }, []);

  const setupEventListener = useCallback(async () => {
    if (!chatCredentials?.channelId || !videoClient) {
      return false;
    }

    const callId = `csb-${chatCredentials.channelId}-1`;
    const expectedCallCid = `default:${callId}`;

    if (eventListenerRef.current) {
      eventListenerRef.current();
      eventListenerRef.current = null;
    }

    try {
      try {
        await videoClient.queryCalls({
          filter_conditions: { ongoing: true },
        });
      } catch (error) {
        reportError(error);
      }

      // listen to client-level events instead of call-level events
      // this way we don't need to initialize the call state
      // if we were to use useCalls, or query for the call, after joining the call, the useCall hook would fail to work
      // this is extremely frustrating, and the root cause is not clear - likely something with Stream's SDK itself
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const handleClientEvent = (event: any) => {
        if (event.call_cid !== expectedCallCid) return;

        switch (event.type) {
          case 'call.notification':
            setIsCallActive(true);
            break;
          case 'call.ended':
            setIsCallActive(false);
            break;
          default:
            break;
        }
      };

      const unsubscribe = videoClient.on('all', handleClientEvent);
      eventListenerRef.current = unsubscribe;

      return true;
    } catch (error) {
      reportError(error);
      return false;
    }
  }, [chatCredentials?.channelId, videoClient]);

  useEffect(() => {
    const setup = async () => {
      const success = await setupEventListener();
      if (!success && setupAttemptsRef.current < 3) {
        retrySetup();
      }
    };

    setup();

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (eventListenerRef.current) {
        eventListenerRef.current();
        eventListenerRef.current = null;
      }
    };
  }, [setupEventListener, retrySetup]);

  if (!isCallActive) {
    return <></>;
  }

  return (
    <div className="w-full py-2 bg-primary flex items-center justify-center gap-2">
      <p className="text-accent font-bold">{t('video_call.psychologistIsCalling')}</p>
      <Button
        className="bg-accent text-primary hover:bg-accent/80 font-bold"
        onClick={async () => {
          try {
            const callId = `csb-${chatCredentials!.channelId}-1`;

            const myCall = videoClient!.call('default', callId);

            await myCall.join({
              create: false,
              ring: false,
            });

            navigate({ to: '/auth/video/call' });
          } catch (error) {
            reportError(error);
          }
        }}
      >
        {t('video_call.joinCallButton')}
      </Button>
    </div>
  );
};
